import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { PageSection } from '@traceio-org/ui';
import type { AdminSelfServeSignup } from '../../api';
import { AdminProblem } from '../../components/AdminProblem';
import { LoadingBlock, SavedNotice } from '../../components/feedback';
import { ReasonField, reasonError } from '../../components/fields';
import { DateTimeText, ScrollTable } from '../../components/records';
import { WriteDialog, useDialogTarget } from '../../components/WriteDialog';
import { useAbilities, useAdmin } from '../../context';
import { plural } from '../../format';
import { useAdminRead } from '../../useAdminRead';
import { SELF_SERVE_STEP_LABELS } from './selfServe';

/** Runs the remaining setup steps of one sign-up again, with the reason recorded in the audit log. */
function RetryDialog({
  signup,
  open,
  onClose,
  onSaved,
  onMoved,
}: {
  signup: AdminSelfServeSignup;
  open: boolean;
  onClose: () => void;
  onSaved: (message: string) => void;
  /** The sign-up is no longer what the list showed (retried by someone else, finished): read the list again. */
  onMoved: () => void;
}) {
  const { api } = useAdmin();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  return (
    <WriteDialog
      open={open}
      onClose={onClose}
      title={`Retry the setup of ${signup.companyName}`}
      description="Runs the steps that are left, starting with the one that failed. The card is saved and nothing is charged until the trial ends."
      submitLabel="Retry the setup"
      onSubmit={async () => {
        const found = reasonError(reason);
        setError(found);
        if (found) return 'invalid';
        await api.retrySelfServeSignup(signup.id, reason);
        return undefined;
      }}
      onSaved={() => onSaved(`The setup of ${signup.companyName} is running again.`)}
      onFailed={(failure) => {
        if (failure.kind === 'conflict' || failure.kind === 'not-found') onMoved();
      }}
    >
      <ReasonField value={reason} onChange={setReason} error={error} />
    </WriteDialog>
  );
}

/**
 * Self-serve sign-ups whose automatic setup failed three times in a row (state needs_attention, spec 2026-10-18
 * piece 4): no automatic retry runs for them, and the founder was told Trace would email them. Staff who read billing
 * see them; staff who manage billing retry one once the cause is fixed. The list is read once per visit and again
 * after a retry.
 */
export function SignupsAttentionSection() {
  const abilities = useAbilities();
  const { api } = useAdmin();
  const read = useAdminRead('self-serve-signups\u0000needs_attention', (signal) =>
    api.selfServeSignups('needs_attention', signal),
  );
  const dialog = useDialogTarget<AdminSelfServeSignup>();
  const [notice, setNotice] = useState<string | null>(null);
  const shown = read.failure ? null : read.data;
  const canRetry = abilities.manageBilling;

  let body: ReactNode;
  if (read.failure) {
    body = (
      <AdminProblem failure={read.failure} subject="the sign-ups needing attention" onRetry={read.retry} />
    );
  } else if (!shown) {
    body = <LoadingBlock label="Loading the sign-ups needing attention" />;
  } else if (shown.items.length === 0) {
    body = <p>No sign-up needs attention.</p>;
  } else {
    body = (
      <ScrollTable caption="Sign-ups needing attention" busy={read.loading} className="admin-table">
        <thead>
          <tr>
            <th scope="col">Company</th>
            <th scope="col">Failed step</th>
            <th scope="col">Last error</th>
            <th scope="col">Needs attention since</th>
            <th scope="col">Organization</th>
            {canRetry ? (
              <th scope="col">
                <span className="admin-sr-only">Actions</span>
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {shown.items.map((signup) => (
            <tr key={signup.id}>
              <th scope="row">
                {signup.companyName}
                <span className="admin-cell-secondary">{signup.email}</span>
              </th>
              <td>
                {signup.failedStep ? SELF_SERVE_STEP_LABELS[signup.failedStep] : 'No step recorded'}
                <span className="admin-cell-secondary">
                  {plural(signup.failureCount, 'failure', 'failures')}
                </span>
              </td>
              <td className="admin-cell-wrap">
                {signup.lastError ?? <span className="admin-muted">No error recorded</span>}
              </td>
              <td>
                {signup.needsAttentionAt ? (
                  <DateTimeText iso={signup.needsAttentionAt} />
                ) : (
                  <span className="admin-muted">Unknown</span>
                )}
              </td>
              <td>
                {signup.organizationId ? (
                  <Link to={`/organizations/${signup.organizationId}/billing`}>Open the organization</Link>
                ) : (
                  <span className="admin-muted">Not created yet</span>
                )}
              </td>
              {canRetry ? (
                <td>
                  <button
                    type="button"
                    className="admin-action"
                    aria-label={`Retry the setup of ${signup.companyName}`}
                    onClick={() => dialog.show(signup)}
                  >
                    Retry
                  </button>
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </ScrollTable>
    );
  }

  return (
    <PageSection
      title="Sign-ups needing attention"
      description="Self-serve sign-ups whose automatic setup failed three times in a row. The card is saved and nothing is charged; the founder was told Trace would email them. Fix the cause, then retry."
    >
      <SavedNotice message={notice} />
      {body}
      {canRetry && dialog.target ? (
        <RetryDialog
          key={dialog.key}
          signup={dialog.target}
          open={dialog.open}
          onClose={dialog.close}
          onSaved={(message) => {
            setNotice(message);
            read.retry();
          }}
          onMoved={read.retry}
        />
      ) : null}
    </PageSection>
  );
}
